import { useContext, useEffect, useState } from 'react';
import { GRANULARITY_TYPES, OVER_TIME_TYPES } from './state.types';
import { ProjectProgressionFiltersContext } from '../../../../state/filters/contexts';
import { getProgressionOverTimeChart } from 'common/services/project-progression.service';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import { useDebounceCallback } from '@react-hook/debounce';
import usePmProvider from 'components/hooks/PM/usePmProvider';
import useComponentLoading from '../../state/useComponentLoading';
import { PROJECT_PROGRESSION_COMPONENTS } from '../../state/components';
import useAutoGranularity from '../../../../state/filters/hooks/useAutoGranularity';

const useProgressionOverTime = () => {
  const {
    state: { isInitialized, period, filtersPayload, extraPayload }
  } = useContext(ProjectProgressionFiltersContext);
  const pmSensorAuthId = usePmSensorAuthId();
  const pmProvider = usePmProvider();
  const [granularity, setGranularity] = useAutoGranularity({
    period,
    granularityTypes: GRANULARITY_TYPES
  });
  const [type, setType] = useState(OVER_TIME_TYPES[pmProvider].ISSUES);
  const [data, setData] = useState([]);
  const [avgVelocity, setAvgVelocity] = useState(null);
  const [tooltipsData, setTooltipsData] = useState([]);
  const [isLoading, setIsLoading] = useComponentLoading(
    PROJECT_PROGRESSION_COMPONENTS.PROGRESSION_OVER_TIME
  );

  const fetchProgressionOverTime = useDebounceCallback(async () => {
    try {
      const res = await getProgressionOverTimeChart({
        sensor_authorization_id: pmSensorAuthId,
        after: period.after,
        aggregation_period: granularity.apiValue,
        type: type.apiValue,
        filtersPayload,
        ...extraPayload
      });
      const resData = Array.isArray(res?.data) ? res.data : [];
      const avgVelocity = res?.average_velocity;
      const tooltipsData = res?.tooltips_data;

      setData(resData);
      setAvgVelocity(avgVelocity);
      setTooltipsData(tooltipsData);
    } catch (e) {
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (!isInitialized) return;
    setIsLoading(true);
    fetchProgressionOverTime();
  }, [isInitialized, granularity, type, period.after, filtersPayload]);

  return {
    data,
    avgVelocity,
    tooltipsData,
    isLoading,
    granularity,
    setGranularity,
    type,
    setType
  };
};

export default useProgressionOverTime;
